// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useTheme } from '@mui/material/styles';
import { STEP_NAME, type StepNameType, cropStepNameToCalendarLabel, utils } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import * as React from 'react';
import { useColorContext } from '../../context/ColorModeContext';
import { Box, Typography } from '../../lib/base';

export interface TaskData {
  readonly id: string;
  readonly task_time: number;
  readonly task_name: string;
  readonly task_status: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
    readonly status: string;
  } | null;
  readonly planting?: {
    readonly id: string;
    readonly order_id?: string | null;
    readonly trays_rounded: string;
  } | null;
}

export interface TaskGroup {
  readonly id: string;
  readonly trays: number;
  readonly tasks: TaskData[];
  readonly traysNotRounded: number;
  readonly isCompleted: boolean;
}

interface TaskBarProps {
  readonly taskGroups: readonly TaskGroup[];
  readonly isForCrop: boolean;
}

export const TaskBar = React.memo(({ taskGroups, isForCrop }: TaskBarProps) => {
  const { mode } = useColorContext();
  const isDarkMode = mode === 'dark';
  const theme = useTheme();
  const oneTask = taskGroups[0]?.tasks[0];
  if (oneTask == null) {
    return null;
  }
  const taskName = oneTask.task_name as StepNameType;
  const isCompleted = taskGroups.every((group) => group.isCompleted);

  // Should be lighter
  const defaultLightColor = theme.custom.colors.blueGrey[300] as string;
  // Should be darker
  const defaultDarkColor = theme.custom.colors.blueGrey[500] as string;

  let color: string = isDarkMode ? defaultDarkColor : defaultLightColor;
  switch (taskName) {
    case STEP_NAME.SOAK:
    case STEP_NAME.PLANT:
    case STEP_NAME.WEIGHTED_GERMINATION:
    case STEP_NAME.PLANT_AND_WEIGHTED_GERM:
    case STEP_NAME.UNWEIGHTED_GERMINATION:
    case STEP_NAME.PLANT_AND_UNWEIGHTED_GERM:
    case STEP_NAME.WATERING:
    case STEP_NAME.BLACKOUT:
    case STEP_NAME.PLANT_AND_BLACKOUT:
    case STEP_NAME.HUMIDITY_DOME:
    case STEP_NAME.PLANT_AND_HUMIDITY_DOME:
    case STEP_NAME.UNDER_LIGHTS:
    case STEP_NAME.HARVEST:
      color = isDarkMode ? theme.custom.tasks[taskName].dark : theme.custom.tasks[taskName].light;
      break;
    default:
      utils.assertNever(taskName);
      break;
  }

  if (!isForCrop) {
    return <EventBar isCompleted={isCompleted} caption={taskName} color={color} />;
  }
  const shortenedTaskName = cropStepNameToCalendarLabel(taskName);
  const totalTrays = taskGroups.reduce((acc, group) => Decimal.add(acc, group.trays), new Decimal(0)).toNumber();

  return <EventBar isCompleted={isCompleted} caption={shortenedTaskName} trayCount={totalTrays} color={color} />;
});
const EventBar = React.memo(
  ({
    caption,
    color,
    isCompleted,
    trayCount,
  }: {
    readonly caption: string;
    readonly color: string;
    readonly isCompleted: boolean;
    readonly trayCount?: number;
  }) => {
    const { mode } = useColorContext();
    const isDarkMode = mode === 'dark';

    return (
      <Box
        id="event-bar-main"
        sx={{
          backgroundColor: color,
          color: isDarkMode ? 'white' : 'black',
          borderRadius: 1,
          height: '22px',
          py: '1px',
          px: 1,
          marginBottom: '2px',
          opacity: isCompleted ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0, // Prevent shrinking
          '@container calendar-day (max-width: 80px)': {
            width: '100%',
            height: '9px',
            mb: '1px',
            padding: 0,
            mt: 0,
            mx: 0,
            display: 'inline-block',
          },
        }}
      >
        <Box
          id="event-bar-inner"
          sx={{
            '@container calendar-day (max-width: 80px)': {
              display: 'none',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              flexGrow: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'hidden',
              textDecoration: isCompleted ? 'line-through' : 'none',
            }}
            variant="caption"
          >
            {caption}
          </Typography>
          {trayCount != null && (
            <Typography
              sx={{
                flexShrink: 0,
              }}
              variant="caption"
            >
              {trayCount}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);
